/**
 * Siapa yang masih dapat dijadikan pekerja — MURNI.
 *
 * Layar Kelola Karyawan menggabungkan dua daftar dari dua sumber: anggota organisasi (milik HBS)
 * dan profil pekerja (milik Workforce). Yang menentukan isi dropdown adalah SELISIH keduanya, dan
 * selisih itulah satu-satunya bagian yang benar-benar bisa salah — karena itu ia di sini, bukan
 * di dalam komponen.
 *
 * Pelajaran yang melatarinya: pada pilot 16 Agustus 2026, absensi seseorang tercatat tetapi tidak
 * pernah muncul di dashboard, karena dashboard membaca roster pekerja dan orangnya tidak ada di
 * sana. Menambahkan orang ke roster harus semudah memilih namanya.
 */

import { namaLayak } from './display-name';

/**
 * Nilai yang DIMILIKI HBS, bukan oleh kode ini — sengaja dibuat tidak bisa dibandingkan langsung.
 *
 * ADA KARENA kelas kesalahan yang sama muncul DUA KALI dalam satu hari (26 Agustus 2026):
 * `taskType.code === 'courier_delivery'` sementara produksi mengirim `DELIVERY`, lalu
 * `m.status === 'ACTIVE'` sementara produksi mengirim `active`. Keduanya tidak menghasilkan galat
 * apa pun — hanya layar kosong dan kalimat yang tidak benar — dan keduanya ditemukan Owner, bukan
 * oleh uji.
 *
 * Pemindai teks TIDAK dipakai untuk menjaganya: percobaannya menjaring 89 tempat yang hampir
 * seluruhnya SAH (`state.status === 'loading'`, `geo.status === 'ready'`) karena nilai-nilai itu
 * milik kita dan sudah dijaga TypeScript sebagai union. Yang membedakan dua bug asli justru
 * tipenya `string` polos, sehingga kompiler tidak punya apa pun untuk ditolak.
 *
 * Jadi yang dipakai adalah KOMPILERNYA: tipe bermerek membuat `m.status === 'ACTIVE'` GAGAL
 * DIKOMPILASI. Satu-satunya jalan memakainya adalah lewat penormal di bawah — yang teruji, dan
 * yang tidak peduli huruf besar-kecil.
 */
declare const MEREK_HBS: unique symbol;
export type NilaiHbs = string & { readonly [MEREK_HBS]: 'dimiliki-hbs' };

export interface Member {
  readonly userId: string;
  readonly displayName: string;
  readonly membershipRole: NilaiHbs;
  readonly status: NilaiHbs;
}

/**
 * Menandai sebuah string sebagai nilai milik HBS — dipakai di BATAS (parsing balasan) dan di uji.
 *
 * Ini satu-satunya pintu masuk, dan itu disengaja: mengetiknya berarti menyatakan "nilai ini
 * datang dari luar dan aku tahu aku tidak boleh membandingkannya mentah-mentah".
 */
export function nilaiHbs(nilai: string): NilaiHbs {
  return nilai as NilaiHbs;
}

/** Satu-satunya cara membandingkan nilai milik HBS: dinormalkan lebih dulu. */
export function samaDenganHbs(nilai: NilaiHbs, harapan: string): boolean {
  return nilai.trim().toLowerCase() === harapan.trim().toLowerCase();
}

export interface WorkerProfile {
  readonly id: string;
  readonly userId: string;
  readonly employeeNumber?: string | null;
  readonly jobTitle?: string | null;
  readonly employmentStatus: string;
}

/**
 * Anggota yang BELUM punya profil pekerja.
 *
 * Anggota tidak aktif dibuang: menawarkan orang yang keanggotaannya sudah dicabut akan membuat
 * admin membuat profil untuk orang yang tidak akan pernah bisa masuk — dan kesalahan itu baru
 * ketahuan berhari-hari kemudian saat orangnya tidak muncul di dashboard.
 *
 * Perbandingannya memakai `userId`, bukan nama: dua orang boleh bernama sama, dan nama dapat
 * berubah di HBS tanpa Workforce tahu.
 */
/**
 * Anggota yang boleh menerima pekerjaan — SATU tempat, dipakai setiap layar yang memilih orang.
 *
 * NILAINYA `'active'` HURUF KECIL, dan itu bukan selera: `status` milik HBS, dikirim apa adanya
 * sebagai string bebas (lihat `http-member-roster.adapter.ts`). Membandingkannya dengan `'ACTIVE'`
 * menyaring HABIS sepuluh anggota yang semuanya aktif — dan layarnya berbunyi "belum ada anggota
 * aktif", kalimat yang terdengar seperti data yang belum diisi, bukan seperti pembanding yang
 * salah huruf. Terjadi 26 Agustus 2026.
 *
 * Dinormalkan, bukan disamakan hurufnya sekali lagi: bila HBS suatu saat mengirim `Active`,
 * saringan ini tidak boleh diam-diam mengosongkan layar lagi.
 */
export function anggotaAktif(members: readonly Member[]): readonly Member[] {
  return members.filter((m) => samaDenganHbs(m.status, 'active'));
}

/**
 * Kurir yang dapat menerima tugas lapangan — daftar yang BOLEH muncul di layar penugasan.
 *
 * ADA KARENA layar penugasan menampilkan SELURUH anggota organisasi: manajer, admin, staf
 * kantor, bahkan Owner. Owner menolaknya 26 Agustus 2026 — "petugasnya cukup kurir saja, jangan
 * semua nama kamu tampilkan" — dan ia benar melampaui soal kerapian: menugaskan pengantaran
 * kepada staf kantor bukan kesalahan yang ditangkap sistem mana pun, ia hanya menghasilkan
 * paket yang tidak pernah berangkat.
 *
 * Dua syarat, keduanya wajib: keanggotaan HBS masih aktif, DAN memegang peran COURIER di
 * Workforce. Peran saja tidak cukup — orang yang keanggotaannya dicabut tetap membawa baris
 * peran lama.
 */
export function kurirTersedia(
  members: readonly Member[],
  roles: readonly { readonly userId: string; readonly role: string }[],
): readonly Member[] {
  const kurir = new Set(roles.filter((r) => r.role === 'COURIER').map((r) => r.userId));
  return anggotaAktif(members).filter((m) => kurir.has(m.userId));
}

export function candidateMembers(
  members: readonly Member[],
  workers: readonly WorkerProfile[],
): readonly Member[] {
  const sudahJadiPekerja = new Set(workers.map((w) => w.userId));
  return members.filter(
    (m) => samaDenganHbs(m.status, 'active') && !sudahJadiPekerja.has(m.userId),
  );
}

/**
 * Nama yang ditampilkan untuk sebuah profil pekerja.
 *
 * Profil menyimpan `userId`, bukan nama — nama milik HBS. Bila anggotanya tidak ditemukan di
 * roster (keanggotaan dicabut setelah profil dibuat), yang ditampilkan BUKAN UUID mentah,
 * melainkan keterangan keadaannya. UUID di layar tidak memberi tahu siapa pun apa pun.
 */
export function workerDisplayName(worker: WorkerProfile, members: readonly Member[]): string {
  const anggota = members.find((m) => m.userId === worker.userId);
  if (!anggota) return 'Anggota tidak ditemukan di organisasi';
  // 'Lengkapi profil', bukan email — lihat namaLayak(). Anggotanya ADA, hanya namanya belum
  // layak ditampilkan; beda dari kasus di atas (anggotanya sendiri sudah tidak ada).
  return namaLayak(anggota.displayName) ?? 'Lengkapi profil';
}

/** Label status kepegawaian dalam bahasa pengguna. */
export const EMPLOYMENT_STATUS_LABEL: Readonly<Record<string, string>> = Object.freeze({
  ACTIVE: 'Aktif',
  INACTIVE: 'Nonaktif',
  SUSPENDED: 'Ditangguhkan',
});

export function employmentStatusLabel(status: string): string {
  return EMPLOYMENT_STATUS_LABEL[status] ?? status;
}
